/*
 * Ingredients — tab panels, each holding an image carousel.
 *
 * The carousel does not loop and does not scroll. Every ingredient is in the
 * strip once, in source order, and JS writes nothing but the index: the
 * stylesheet slides the strip so the active ingredient lands in its slot. At
 * the first ingredient there is nothing behind it, so the slots to its left
 * are simply empty — the strip is never wrapped round to fill them.
 *
 * The slot count and the slot width both live in section-ingredients.css, so
 * the media query there stays the single source of truth for how the two
 * frames differ and this file never measures anything.
 *
 * Arrows are conventional: left goes back, right goes forward, and the left
 * one starts disabled. They were reversed for a while (the left glyph advanced,
 * following the strip's leftward travel) and reverted on 2026-09-13 at the
 * client's request. Swipe was never reversed — a leftward swipe advances.
 */
class IngredientsTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('.ing__tab'));
    this.panels = Array.from(this.querySelectorAll('.ing__panel'));
    this.tabStrip = this.querySelector('.ing__tabs');
    if (!this.panels.length) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.selectTab(i));
      tab.addEventListener('keydown', (event) => this.onTabKey(event, i));
    });

    this.carousels = this.panels.map((panel) => this.setupCarousel(panel));

    /* No resize listener: the slot count and slot width are both custom
       properties, so the breakpoint re-lays the strip on its own. */

    const preselected = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    this.selectTab(preselected === -1 ? 0 : preselected);
    this.ready = true;
  }

  /* Tabs ------------------------------------------------------------------ */

  selectTab(index) {
    const target = Math.max(0, Math.min(this.tabs.length - 1, index));

    this.tabs.forEach((tab, i) => {
      const on = i === target;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      // Roving tabindex: only the selected tab is a tab stop, arrow keys move
      // between the rest.
      tab.tabIndex = on ? 0 : -1;
    });

    this.panels.forEach((panel, i) => {
      panel.toggleAttribute('hidden', i !== target);
    });

    this.current = target;

    const carousel = this.carousels[target];
    if (carousel) this.paint(carousel);

    const tab = this.tabs[target];
    if (tab) this.revealTab(tab);
  }

  /* Brings the selected tab into view inside the tab strip when the row is wide
     enough to overflow.
     Deliberately NOT scrollIntoView: it walks every scrollable ancestor, so
     `block: 'nearest'` scrolls the *page* as well. selectTab runs once on
     connect, which meant a visitor landing on the home page was thrown straight
     down to this section before they had scrolled at all.
     Rects rather than offsetLeft — the strip is not every tab's offsetParent
     (failsafe Topic 10). */
  revealTab(tab) {
    const strip = this.tabStrip;
    if (!strip || strip.scrollWidth <= strip.clientWidth) return;

    const view = strip.getBoundingClientRect();
    const box = tab.getBoundingClientRect();
    const style = getComputedStyle(strip);
    const padLeft = parseFloat(style.scrollPaddingLeft) || 0;
    const padRight = parseFloat(style.scrollPaddingRight) || 0;

    let delta = 0;
    if (box.left < view.left + padLeft) delta = box.left - view.left - padLeft;
    else if (box.right > view.right - padRight) delta = box.right - view.right + padRight;
    if (!delta) return;

    strip.scrollBy({
      left: delta,
      // No animation for the paint that happens on connect — there is nothing
      // to follow yet, and it would run before the section is even on screen.
      behavior: this.ready && !this.reduceMotion.matches ? 'smooth' : 'auto',
    });
  }

  onTabKey(event, index) {
    const keys = { ArrowLeft: -1, ArrowRight: 1, Home: 'first', End: 'last' };
    const action = keys[event.key];
    if (action === undefined) return;

    event.preventDefault();
    const last = this.tabs.length - 1;
    let next;
    if (action === 'first') next = 0;
    else if (action === 'last') next = last;
    else next = (index + action + this.tabs.length) % this.tabs.length;

    this.selectTab(next);
    this.tabs[next].focus();
  }

  /* Carousel -------------------------------------------------------------- */

  setupCarousel(panel) {
    const strip = panel.querySelector('.ing__strip');
    if (!strip) return null;

    const carousel = {
      panel,
      strip,
      items: Array.from(strip.querySelectorAll('.ing__item')),
      copies: Array.from(panel.querySelectorAll('.ing__copy-item')),
      index: 0,
    };
    if (!carousel.items.length) return null;

    carousel.items.forEach((item, i) => {
      const button = item.querySelector('.ing__circle');
      if (button) button.addEventListener('click', () => this.move(carousel, i));
    });

    // Right goes forward, left goes back. Held on the carousel so updateArrows
    // can reach them.
    carousel.forward = panel.querySelector('.ing__arrow--right');
    carousel.back = panel.querySelector('.ing__arrow--left');
    if (carousel.forward) carousel.forward.addEventListener('click', () => this.step(carousel, 1));
    if (carousel.back) carousel.back.addEventListener('click', () => this.step(carousel, -1));

    this.setupSwipe(carousel);
    this.paint(carousel);
    return carousel;
  }

  // Clamped, not wrapped: the last ingredient is the end of the strip.
  step(carousel, delta) {
    const last = carousel.items.length - 1;
    this.move(carousel, Math.max(0, Math.min(last, carousel.index + delta)));
  }

  /*
   * Disabling the button under the cursor is fine; disabling the one under the
   * *keyboard* is not — focus would fall to the body and a keyboard visitor
   * would lose their place mid-carousel. So hand focus to the sibling first,
   * with preventScroll: the pair sits below the fold of a tall section and a
   * plain focus() would drag the page to it.
   */
  updateArrows(carousel) {
    const last = carousel.items.length - 1;
    const states = [
      [carousel.forward, carousel.index >= last, carousel.back],
      [carousel.back, carousel.index <= 0, carousel.forward],
    ];

    states.forEach(([button, off, sibling]) => {
      if (!button) return;
      if (off && button === document.activeElement && sibling && !sibling.disabled) {
        sibling.focus({ preventScroll: true });
      }
      button.disabled = off;
    });
  }

  /*
   * Horizontal swipe over the strip. `touch-action: pan-y` is not set here on
   * purpose — the strip does not scroll, so the browser's vertical panning can
   * be left completely alone and only the end-to-end delta is read.
   */
  setupSwipe(carousel) {
    const viewport = carousel.panel.querySelector('.ing__viewport');
    if (!viewport) return;

    let startX = null;

    viewport.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startX = event.clientX;
      },
      { passive: true }
    );

    const finish = (event) => {
      if (startX === null) return;
      const delta = event.clientX - startX;
      startX = null;
      if (Math.abs(delta) < 40) return;
      this.step(carousel, delta < 0 ? 1 : -1);
    };

    viewport.addEventListener('pointerup', finish, { passive: true });
    viewport.addEventListener('pointercancel', () => {
      startX = null;
    });
  }

  move(carousel, index) {
    if (index === carousel.index) return;
    carousel.index = index;
    this.paint(carousel);
  }

  /*
   * Publishes the index and lets the stylesheet do the rest. The strip's
   * translate and the active circle's width are both CSS transitions off the
   * same --ing-speed, so there is no FLIP pass to run and nothing to measure —
   * which is also why this survives a resize across the breakpoint untouched.
   */
  paint(carousel) {
    carousel.strip.style.setProperty('--ing-index', String(carousel.index));

    carousel.items.forEach((item, i) => {
      const active = i === carousel.index;
      item.classList.toggle('ing__item--active', active);

      const button = item.querySelector('.ing__circle');
      // Deliberately not `disabled`: the circle you just clicked becomes the
      // active one, and disabling it would drop the focus you are standing on.
      if (button) button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    carousel.copies.forEach((copy, i) => {
      copy.classList.toggle('ing__copy-item--active', i === carousel.index);
    });

    this.updateArrows(carousel);
  }
}

customElements.define('ingredients-tabs', IngredientsTabs);
